'use client';
import { useState, useRef } from 'react';
import type { ScoredStock, PillarScores } from '@/lib/compute';
import { factorLabel } from '@/lib/clientScore';
import { getSectorColor } from '@/lib/sector-colors';
import {
  buildDrillFactors, fmtScore, fmtFactorVal,
  type DrilldownTarget,
} from '@/lib/scatter2d-helpers';

interface ComparePanelProps {
  pinnedStocks: ScoredStock[];
  maxPins: number;
  onTogglePin: (symbol: string) => void;
}

/**
 * Floating draggable comparison table for pinned stocks. Two views:
 *   1. Summary — pillar scores per pinned stock; columns are drillable
 *   2. Drilldown — per-factor breakdown for the chosen pillar
 */
export function ComparePanel({ pinnedStocks, maxPins, onTogglePin }: ComparePanelProps) {
  const [drilldown, setDrilldown] = useState<DrilldownTarget>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; startPosX: number; startPosY: number } | null>(null);

  const columns: { id: string; label: string; drillable: boolean }[] = [
    { id: 'quality', label: 'Quality', drillable: false },
    { id: 'valuation', label: 'Value', drillable: true },
    { id: 'profitability', label: 'Profit', drillable: true },
    { id: 'growth', label: 'Growth', drillable: true },
    { id: 'safety', label: 'Safety', drillable: true },
    { id: 'shareholderReturns', label: 'Shr. Returns', drillable: true },
  ];

  const getCellValue = (s: ScoredStock, colId: string): number | null => {
    if (colId === 'quality') return s.qualityScore;
    if (colId === 'valuation') return s.valuationScore;
    return s.pillarScores[colId as keyof PillarScores] ?? null;
  };

  const getCellColor = (colId: string): string => {
    if (colId === 'quality') return '#4ade80';
    if (colId === 'valuation') return '#60a5fa';
    return '#cbd5e1';
  };

  const drillFactors = drilldown ? buildDrillFactors(drilldown, pinnedStocks) : [];

  const handleDragStart = (e: React.MouseEvent) => {
    const el = (e.target as HTMLElement).closest('[data-drag-handle]');
    if (!el) return;
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const currentX = pos?.x ?? rect.left;
    const currentY = pos?.y ?? rect.top;
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPosX: currentX, startPosY: currentY };

    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      setPos({
        x: dragRef.current.startPosX + (ev.clientX - dragRef.current.startX),
        y: dragRef.current.startPosY + (ev.clientY - dragRef.current.startY),
      });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div
      className={pos ? 'fixed z-10 rounded-xl p-5 border border-[#1e293b]' : 'absolute bottom-4 right-4 z-10 rounded-xl p-5 border border-[#1e293b]'}
      style={{
        background: 'rgba(13, 17, 23, 0.93)',
        backdropFilter: 'blur(10px)',
        maxWidth: drilldown ? 700 : 600,
        ...(pos ? { left: pos.x, top: pos.y } : {}),
      }}
      onMouseDown={handleDragStart}
    >
      <div className="flex items-center justify-between mb-3" data-drag-handle style={{ cursor: 'grab' }}>
        <div className="flex items-center gap-3">
          {drilldown && (
            <button
              onClick={() => setDrilldown(null)}
              className="text-[13px] text-blue-400 hover:text-blue-300 cursor-pointer"
            >
              &larr; Back
            </button>
          )}
          <span className="text-[13px] font-semibold text-white select-none">
            {drilldown ? `${drilldown.charAt(0).toUpperCase() + drilldown.slice(1)} Factors` : `Compare (${pinnedStocks.length}/${maxPins})`}
          </span>
        </div>
        <button
          onClick={() => pinnedStocks.forEach(s => onTogglePin(s.symbol))}
          className="text-[12px] text-blue-400 hover:text-blue-300 transition-colors duration-150 cursor-pointer"
        >
          Clear all
        </button>
      </div>

      {!drilldown ? (
        <table className="w-full" style={{ fontFamily: 'var(--font-mono), ui-monospace, monospace' }}>
          <thead>
            <tr>
              <th className="text-left pr-4 pb-2 text-[13px] font-semibold text-[#e2e8f0]">Symbol</th>
              {columns.map(col => (
                <th key={col.id}
                  className={`text-right px-2 pb-2 text-[13px] font-semibold text-[#e2e8f0]
                             ${col.drillable ? 'cursor-pointer hover:text-white transition-colors' : ''}`}
                  onClick={col.drillable ? () => setDrilldown(col.id as DrilldownTarget) : undefined}
                >
                  {col.label}{col.drillable ? ' ›' : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pinnedStocks.map(s => (
              <tr key={s.symbol}>
                <td className="pr-4 py-1.5">
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: getSectorColor(s.sector) }} />
                    <span className="text-white font-bold text-[14px]">{s.symbol}</span>
                  </span>
                </td>
                {columns.map(col => (
                  <td key={col.id}
                    className={`text-right px-2 py-1.5 text-[14px] font-semibold
                               ${col.drillable ? 'cursor-pointer hover:underline' : ''}`}
                    style={{ color: getCellColor(col.id) }}
                    onClick={col.drillable ? () => setDrilldown(col.id as DrilldownTarget) : undefined}
                  >
                    {fmtScore(getCellValue(s, col.id))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" style={{ fontFamily: 'var(--font-mono), ui-monospace, monospace' }}>
            <thead>
              <tr>
                <th className="text-left pr-3 pb-2 text-[12px] font-semibold text-[#e2e8f0]">Factor</th>
                {pinnedStocks.map(s => (
                  <th key={s.symbol} className="text-right px-2 pb-2 text-[13px] font-bold text-white min-w-[70px]">
                    {s.symbol}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[#1e293b]">
                <td className="pr-3 py-1.5 text-[12px] font-semibold text-white italic">
                  {drilldown} score
                </td>
                {pinnedStocks.map(s => {
                  const val = drilldown === 'valuation' ? s.valuationScore : s.pillarScores[drilldown as keyof PillarScores];
                  return (
                    <td key={s.symbol} className="text-right px-2 py-1.5 text-[13px] font-bold"
                      style={{ color: getCellColor(drilldown) }}>
                      {fmtScore(val ?? null)}
                    </td>
                  );
                })}
              </tr>
              {drillFactors.map(({ key }) => (
                <tr key={key}>
                  <td className="pr-3 py-1 text-[11px] text-[#e2e8f0]">{factorLabel(key)}</td>
                  {pinnedStocks.map(s => (
                    <td key={s.symbol} className="text-right px-2 py-1 text-[12px] text-[#e2e8f0]">
                      {fmtFactorVal(s.factors[key], key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
